import { FC, useEffect, useMemo } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { DsmButton, DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import { FormType, ListEntry } from "../../common";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { enumToOptionsArrayWithTranslatedStrings } from "../../../../utils/obj-utils";
import { StageType, TransportMode } from "../../../../../../graphql/types";
import SustellTypeAmountFields from "../../../../../modules/Helpers/SustellTypeAmountFIelds";
import {
  ShrimpBaseline,
  ShrimpOutput,
  ShrimpProcessing,
  ShrimpProcessingInputType,
  ShrimpStage,
  TransportTypeDistanceIce,
} from "../../../../models/Baseline/ShrimpBaseline";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import {
  convertOuncesToGrams,
  convertPoundsToKilograms,
} from "../../../../helpers/calcHelpers";

const DEFAULT_EMPTY_TRANSPORT = {
  transportMode: "",
  distance: "",
  iceAmount: "",
};

export enum ShrimpUnitsEnum {
  Kilograms = "kg",
  Pounds = "lbs",
  Grams = "g",
  Ounce = "oz",
}

interface ShrimpAnimalsFormSourceContentProps {
  itemIndex: number;
  fieldItemPrefix: string;
  formType: FormType;
  averageWeightUnit: ShrimpUnitsEnum;
  handleSourceRemoveClick: (index: number) => void;
}

const ShrimpAnimalsFormSourceContent: FC<
  ShrimpAnimalsFormSourceContentProps
> = ({
  itemIndex = 0,
  fieldItemPrefix,
  formType,
  averageWeightUnit,
  handleSourceRemoveClick,
}) => {
    const intl = useIntl();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const classes = processAndStageStylesV2() as any;
    const formContext = useFormContext<ShrimpBaseline>();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const barnOutputMassUnit =
      userUOM?.unitBarnOutputMass &&
        (userUOM?.unitBarnOutputMass === ShrimpUnitsEnum.Kilograms ||
          userUOM?.unitBarnOutputMass === ShrimpUnitsEnum.Pounds)
        ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        unitLong(userUOM.unitBarnOutputMass)
        : intl.formatMessage({
          id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.ICE_AMOUNT.ADORNMENT",
        }) || "";

    const originalStageIdWatch = formContext.watch(
      `${fieldItemPrefix}.sources.${itemIndex}.originStageId`
    ) as string;

    const selectedOriginOutputWatch = formContext.watch(
      `${fieldItemPrefix}.sources.${itemIndex}.originOutputId`
    ) as string;

    const stagesWatch = formContext.watch("stages") as (
      | ShrimpStage
      | ShrimpProcessing
    )[];

    const {
      fields: transport,
      append: appendTransport,
      remove: removeTransport,
    } = useFieldArray({
      name: `${fieldItemPrefix}.sources.${itemIndex}.transport`,
      control: formContext.control,
      keyName: "keyId",
    });

    useEffect(() => {
      if (!transport?.length) appendTransport(DEFAULT_EMPTY_TRANSPORT);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalWeightWatch = useWatch({
      control: formContext.control,
      name: `${fieldItemPrefix}.sources.${itemIndex}.totalWeight`,
    });

    const averageWeightWatch = useWatch({
      control: formContext.control,
      name: `${fieldItemPrefix}.averageWeight`,
    });

    const shrimpQuantityCalculation = Number(totalWeightWatch) / (Number(averageWeightWatch) / 1000);

    const transportWatch = useWatch({
      control: formContext.control,
      name: `${fieldItemPrefix}.sources.${itemIndex}.transport`,
    }) as TransportTypeDistanceIce[];

    useEffect(() => {
      const totalWeight = Number(totalWeightWatch);
      const averageWeight = Number(averageWeightWatch);

      const isValidWeight = totalWeight > 0;
      const isValidAverage = averageWeight > 0;

      if (isValidWeight && isValidAverage) {
        const totalWeightKg =
          barnOutputMassUnit === ShrimpUnitsEnum.Pounds
            ? convertPoundsToKilograms(totalWeight)
            : totalWeight;

        const averageWeightG =
          averageWeightUnit === ShrimpUnitsEnum.Ounce
            ? convertOuncesToGrams(averageWeight)
            : averageWeight;

        const totalWeightG = totalWeightKg * 1000;
        const estimatedQuantity = Math.floor(totalWeightG / averageWeightG);

        formContext.setValue(
          `${fieldItemPrefix}.sources.${itemIndex}.quantity`,
          estimatedQuantity
        );
      } else {
        formContext.setValue(
          `${fieldItemPrefix}.sources.${itemIndex}.quantity`,
          ""
        );
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalWeightWatch, averageWeightWatch, itemIndex]);

    const allTransportOptions = useMemo(
      () =>
        enumToOptionsArrayWithTranslatedStrings(
          TransportMode,
          intl,
          "SUSTELL.TRANSPORT_MODE"
        ),
      [intl]
    );

    const isAddTransportDisabled = () => {
      const selectedModes = transportWatch?.map((t) => t.transportMode as string);
      const availableOptions = allTransportOptions.filter(
        (option) => !selectedModes?.includes(option.value)
      );

      return availableOptions.length === 0;
    };

    const getFilteredTransportTypeOptions = (index: number) => {
      const selectedTransportForIndex = transportWatch?.[index].transportMode;
      const selectedModes = transportWatch
        ?.filter((t) => t.transportMode !== selectedTransportForIndex)
        .map((t) => t.transportMode as string);
      return allTransportOptions.filter(
        (option) => !selectedModes?.includes(option.value)
      );
    };

    const getSources = (): ShrimpProcessingInputType[] =>
      formContext.getValues(`${fieldItemPrefix}.sources`) ?? [];

    const getStageById = (stageId: string): ShrimpStage | undefined => {
      const stages = formContext.getValues("stages") ?? [];
      return stages.find((stage) => stage.id === stageId) as ShrimpStage;
    };

    const getSelectedOutputsForStage = (stageId: string): string[] =>
      getSources()
        ?.filter((source) => source.originStageId === stageId)
        .map((source) => source.originOutputId);

    const isValidOutput = (output: ShrimpOutput): boolean => {
      if (output.name == "" || (output.totalWeight == "" || null))
        return false;

      return true;
    }

    const availableOutputs = (): ListEntry[] => {
      if (!originalStageIdWatch) return [];

      const selectedStage = getStageById(originalStageIdWatch);
      if (!selectedStage) return [];

      const outputs = selectedStage.stageData?.output?.outputs ?? [];
      const selectedOutputIds = getSelectedOutputsForStage(selectedStage.id);
      const filteredOutputIds = selectedOutputIds.filter(
        (id) => id !== selectedOriginOutputWatch
      );

      return outputs
        .filter((output) => !filteredOutputIds.includes(output.id) && isValidOutput(output))
        .map(
          (output) =>
          ({
            text: output.name,
            value: output.id,
          } as ListEntry)
        );
    };

    const outputOptions: ListEntry[] = availableOutputs();

    const mountStages = (stages: Array<ShrimpStage | ShrimpProcessing>) => {
      if (!stages || !stages.length) return [];
      return stages.map((stage) => ({
        text: stage.name,
        value: stage.id,
      }));
    };

    const allAvailableStages = useMemo(
      () =>
        stagesWatch
          .filter((stage) => stage.type === StageType.Growing)
          ?.map((stage) => ({
            text: stage.name,
            value: stage.id,
          })),
      [stagesWatch]
    );

    const getStagesWithAtLeastOneUnusedOutput = () => {
      const allStages = formContext.getValues("stages");
      const allAvailableStages = allStages.filter(
        (stage) => stage.type === StageType.Growing
      );

      const stagesWithUnusedOutputs = allAvailableStages.filter((stage) => {
        const outputs = (stage as ShrimpStage)?.stageData?.output
          ?.outputs as ShrimpOutput[];
        const selectedOutputs = getSelectedOutputsForStage(stage.id);

        if (!outputs?.length) return false;

        return outputs.some((output) => !selectedOutputs.includes(output.id));
      });

      return mountStages(stagesWithUnusedOutputs);
    };

    const resetOutput = () => {
      formContext.setValue(
        `${fieldItemPrefix}.sources.${itemIndex}.originOutputId`,
        ""
      );
    };

    const getTotalWeightFromOriginOutput = (originStageId: string, originOutputId: string): number => {
      const allStages = formContext.getValues("stages");
      const allAvailableStages = allStages.filter(
        (stage) => stage.type === StageType.Growing
      );

      const currentStage: ShrimpStage = allAvailableStages.find((stage) => stage.id === originStageId) as ShrimpStage;

      if (currentStage) {
        const currentOutput: ShrimpOutput = currentStage.stageData.output.outputs.find((output) => output.id === originOutputId) as ShrimpOutput;

        if (currentOutput) {
          return currentOutput.totalWeight as number;
        }
      }
      return 0;
    }

    return (
      <>
        <input
          ref={formContext.register()}
          type="hidden"
          name={`${fieldItemPrefix}.sources.${itemIndex}.id`}
          value={formContext.getValues(
            `${fieldItemPrefix}.sources.${itemIndex}.id`
          )}
        />
        <input
          ref={formContext.register()}
          type="hidden"
          name={`${fieldItemPrefix}.sources.${itemIndex}.quantity`}
          value={formContext.getValues(
            `${fieldItemPrefix}.sources.${itemIndex}.quantity`
          )}
        />
        <input
          ref={formContext.register()}
          type="hidden"
          name={`${fieldItemPrefix}.sources.${itemIndex}.originTotalWeight`}
          value={getTotalWeightFromOriginOutput(
            formContext.getValues(
              `${fieldItemPrefix}.sources.${itemIndex}.originStageId`
            ),
            formContext.getValues(
              `${fieldItemPrefix}.sources.${itemIndex}.originOutputId`
            )
          )}
        />
        <input
          ref={formContext.register()}
          type="hidden"
          name={`${fieldItemPrefix}.sources.${itemIndex}.outputLength`}
          value={outputOptions.length ?? 0}
        />
        <DsmGrid
          className={classes.dsmGridOneColumnInterventionWithBorder}
          style={{
            padding: "var(--dsm-spacing-px-4)",
            marginBottom: "var(--dsm-spacing-px-2)",
            rowGap: "var(--dsm-spacing-px-2)",
            position: "relative",
          }}
        >
          <DsmGrid className={classes.dsmGridTwoColumnNoRowSpacing}>
            <ReactHookDsmSelect
              style={{ marginBottom: "12px" }}
              name={`${fieldItemPrefix}.sources.${itemIndex}.originStageId`}
              label={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.FROM_STAGE",
              })}
              tooltip={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.FROM_STAGE.TOOLTIP",
              })}
              disabled={formType === FormType.View}
              adornment={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.FROM_STAGE.PLACEHOLDER",
              })}
              placeholder={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.FROM_STAGE.PLACEHOLDER",
              })}
              options={
                !!originalStageIdWatch
                  ? allAvailableStages
                  : getStagesWithAtLeastOneUnusedOutput()
              }
              required
              defaultValue={formContext.getValues(
                `${fieldItemPrefix}.sources.${itemIndex}.originStageId`
              )}
              changeHandler={resetOutput}
            />

            <ReactHookDsmSelect
              style={{ marginBottom: "12px" }}
              name={`${fieldItemPrefix}.sources.${itemIndex}.originOutputId`}
              label={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.OUTPUT_NAME",
              })}
              tooltip={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.OUTPUT_NAME.TOOLTIP",
              })}
              disabled={formType === FormType.View}
              adornment={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.OUTPUT_NAME.PLACEHOLDER",
              })}
              options={outputOptions}
              required
              defaultValue={formContext.getValues(
                `${fieldItemPrefix}.sources.${itemIndex}.originOutputId`
              )}
              changeHandler={() => {}}
            />

            <ReactHookDsmInput
              name={`${fieldItemPrefix}.sources.${itemIndex}.totalWeight`}
              label={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.TOTAL_WEIGHT",
              })}
              disabled={formType === FormType.View}
              tooltip={intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.TOTAL_WEIGHT.TOOLTIP",
              })}
              adornment={barnOutputMassUnit}
              required
              type="number"
              defaultValue={formContext.getValues(
                `${fieldItemPrefix}.sources.${itemIndex}.totalWeight`
              )}
              helperText={intl.formatMessage(
                {
                  id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.TOTAL_WEIGHT.HELPER",
                },
                {
                  amount: shrimpQuantityCalculation
                    ? shrimpQuantityCalculation
                    : "",
                }
              )}
            />
          </DsmGrid>

          <DsmGrid
            className={classes.dsmGridOneColumn}
            style={{ gap: "var(--dsm-spacing-px-2)" }}
          >
            <span className={classes.headingTitleVariant}>
              {intl.formatMessage({
                id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.TRANSPORT",
              })}
            </span>

            {transport?.map((item, index) => (
              <DsmGrid
                className={classes.dsmGridTwoColumnNoRowSpacingWithBorder}
                style={{
                  padding: "var(--dsm-spacing-px-4)",
                  position: "relative",
                  rowGap: "var(--dsm-spacing-px-4)",
                }}
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                key={`key_transport_${item.keyId}`}
              >
                <SustellTypeAmountFields
                  labelInput={intl.formatMessage({
                    id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.DISTANCE_TRAVELED",
                  })}
                  labelSelect={intl.formatMessage({
                    id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.TRANSPORT_MODE",
                  })}
                  placeholderSelect={intl.formatMessage({
                    id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.TRANSPORT_MODE.PLACEHOLDER",
                  })}
                  options={getFilteredTransportTypeOptions(index)}
                  index={index}
                  formType={formType}
                  fieldItemPrefix={`${fieldItemPrefix}.sources.${itemIndex}.transport`}
                  typeFieldName="transportMode"
                  amountFieldName="distance"
                  deafultValueType={formContext.getValues(
                    `${fieldItemPrefix}.sources.${itemIndex}.transport.${index}.transportMode`
                  )}
                  defaultValueAmount={formContext.getValues(
                    `${fieldItemPrefix}.sources.${itemIndex}.transport.${index}.distance`
                  )}
                  placeholderUnit="km"
                  keyName="sl-animals-transports"
                  tooltipType={intl.formatMessage({
                    id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.DISTANCE_TRAVELED.TOOLTIP",
                  })}
                  tooltipUse={intl.formatMessage({
                    id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.TRANSPORT_MODE.TOOLTIP",
                  })}
                />
                <ReactHookDsmInput
                  defaultValue={formContext.getValues(
                    `${fieldItemPrefix}.sources.${itemIndex}.transport.${index}.iceAmount`
                  )}
                  key={`key-for-iceAmount-${itemIndex || ""}^-${index || ""}`}
                  name={`${fieldItemPrefix}.sources.${itemIndex}.transport.${index}.iceAmount`}
                  label={intl.formatMessage({
                    id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.ICE_AMOUNT",
                  })}
                  type="number"
                  tooltip={intl.formatMessage({
                    id: "BASELINE.FORM.PROCESSING.SHRIMP.ANIMALS.ICE_AMOUNT.TOOLTIP",
                  })}
                  adornment={barnOutputMassUnit}
                  disabled={formType === FormType.View}
                />
                {formType !== FormType.View && index !== 0 && (
                  <DsmButton
                    variant="text"
                    style={{ width: "100%" }}
                    onClick={() => removeTransport(index)}
                  >
                    <DsmIcon
                      name="general/x-close"
                      style={{
                        position: "absolute",
                        color: "var(--dsm-color-neutral-darker",
                        right: "0",
                        top: "0",
                        margin: "6px 6px 0px 0px",
                      }}
                    />
                  </DsmButton>
                )}
              </DsmGrid>
            ))}

            <DsmButton
              variant="text"
              onClick={() => appendTransport(DEFAULT_EMPTY_TRANSPORT)}
              disabled={formType === FormType.View || isAddTransportDisabled()}
              style={{
                width: "max-content",
              }}
            >
              <DsmIcon slot="before" name="general/plus-circle" />
              {intl.formatMessage({
                id: "SUSTELL.FACILITIES.RESOURCES.ADD_ANOTHER_TRANSPORT_MODE",
              })}
            </DsmButton>
          </DsmGrid>

          {formType !== FormType.View && itemIndex !== 0 && (
            <DsmButton
              variant="text"
              style={{
                position: "absolute",
                top: "6px",
                right: "6px",
                padding: 0,
                minWidth: "unset",
              }}
              onClick={() => {
                handleSourceRemoveClick(itemIndex);
              }}
            >
              <DsmIcon
                name="general/x-close"
                style={{ color: "var(--dsm-color-neutral-darker" }}
              />
            </DsmButton>
          )}
        </DsmGrid>
      </>
    );
  };

export default ShrimpAnimalsFormSourceContent;
